import { Rng } from '../core/rng.js'
import { CELL, GROUND, SCENERY, TERRAIN, World, sceneryWidth } from './world.js'

function settlementSites(x: number, y: number, direction: number): number[][] {
  return [[x + 11 * direction, y - 3 * direction, SCENERY.House], [x - 3 * direction, y - 11 * direction, SCENERY.Warehouse],
    [x + 11 * direction, y + 3 * direction, SCENERY.House], [x - 11 * direction, y + 3 * direction, SCENERY.Tank],
    [x - 11 * direction, y - 3 * direction, SCENERY.Cargo]]
}

/** Settled ground around each base, with two approaches on either side. */
export function layRoads(world: World): void {
  for (let p = 0; p < world.starts.length; p += 2) {
    const sx = world.starts[p], sy = world.starts[p + 1]
    if (sx < 0) continue
    for (let y = sy - 10; y <= sy + 10; y++) {
      for (let x = sx - 10; x <= sx + 10; x++) {
        const dx = Math.abs(x - sx), dy = Math.abs(y - sy)
        const edge = Math.max(dx, dy)
        if (edge < 5 || edge > 9) continue
        const i = y * world.size + x
        world.height[i] = 0
        world.flags[i] = CELL.Passable
        world.terrain[i] = TERRAIN.Dust
        if (edge >= 6 && edge <= 8) world.ground[i] = GROUND.Road
      }
    }
  }
  // Reserve the parcels before ore is placed, including their walkable perimeter.
  for (let p = 0; p < world.starts.length; p += 2) {
    const sx = world.starts[p], sy = world.starts[p + 1]
    if (sx < 0) continue
    for (const [x, y, kind] of settlementSites(sx, sy, p % 4 === 0 ? 1 : -1)) {
      const radius = (sceneryWidth(kind) + 1) / 2
      for (let dy = -radius; dy <= radius; dy++) for (let dx = -radius; dx <= radius; dx++) {
        const i = (y + dy) * world.size + x + dx
        world.height[i] = 0
        world.flags[i] = CELL.Passable
        world.terrain[i] = TERRAIN.Dust
        world.ground[i] = GROUND.Paving
      }
    }
  }
}

/** Every obstacle retains a passable perimeter, so it cannot sever a route. */
function sceneryFits(world: World, x: number, y: number, kind: number): boolean {
  const radius = (sceneryWidth(kind) - 1) / 2
  const level = world.levelAt(x, y)
  for (let dy = -radius - 1; dy <= radius + 1; dy++) {
    for (let dx = -radius - 1; dx <= radius + 1; dx++) {
      const cx = x + dx, cy = y + dy, i = cy * world.size + cx
      if (!world.inBounds(cx, cy) || !world.isPassable(cx, cy) || world.height[i] !== level) return false
      if ((world.flags[i] & (CELL.Field | CELL.Ramp | CELL.Scenery)) !== 0) return false
      if (Math.abs(dx) <= radius && Math.abs(dy) <= radius
        && world.ground[i] !== GROUND.Natural && !(kind !== SCENERY.Tree && world.ground[i] === GROUND.Paving)) return false
    }
  }
  return true
}

export function placeScenery(world: World, x: number, y: number, kind: number): boolean {
  if (!sceneryFits(world, x, y, kind)) return false
  const radius = (sceneryWidth(kind) - 1) / 2
  world.scenery[y * world.size + x] = kind
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      const i = (y + dy) * world.size + x + dx
      world.flags[i] = (world.flags[i] & ~CELL.Passable) | CELL.Scenery
      if (kind !== SCENERY.Tree) world.ground[i] = GROUND.Paving
    }
  }
  return true
}

export function dressLandscape(world: World, seed: string | number): void {
  const rng = new Rng(`${seed}:landscape`), size = world.size
  function pair(x: number, y: number, kind: number): void {
    // Odd-player maps can have asymmetric start clearings. Check both before writing.
    if (!sceneryFits(world, size - 1 - x, size - 1 - y, kind)) return
    if (!placeScenery(world, x, y, kind)) return
    placeScenery(world, size - 1 - x, size - 1 - y, kind)
  }
  for (let p = 0; p < world.starts.length; p += 2) {
    const sx = world.starts[p], sy = world.starts[p + 1]
    if (sx < 0) continue
    const direction = p % 4 === 0 ? 1 : -1
    for (const [x, y, kind] of settlementSites(sx, sy, direction)) placeScenery(world, x, y, kind)
    for (let dy = -1; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
      const x = sx + dx * direction, y = sy + (dy - 11) * direction, i = y * size + x
      if (world.isPassable(x, y) && world.height[i] === 0 && (world.flags[i] & (CELL.Field | CELL.Ramp)) === 0) {
        world.ground[i] = GROUND.Parking
      }
    }
  }
  for (let y = 3; y < size / 2 - 3; y += 2) {
    for (let x = 3; x < size - 3; x += 2) {
      const cx = x + rng.int(2), cy = y + rng.int(2)
      let nearStart = false
      for (let p = 0; p < world.starts.length; p += 2) {
        if (world.starts[p] < 0) continue
        const dx = cx - world.starts[p], dy = cy - world.starts[p + 1]
        if (dx * dx + dy * dy < 12 * 12) nearStart = true
      }
      if (nearStart) continue
      // Low-frequency stands, with open meadows between them.
      const grove = (Math.imul(cx >> 3, 73856093) ^ Math.imul(cy >> 3, 19349663)) >>> 0
      if (grove % 5 > 1 || rng.int(4) === 0) continue
      pair(cx, cy, SCENERY.Tree)
    }
  }
}
